import type { ConfigurationFormFieldsObject } from 'src/pages/ConfigurationPage/hooks/useConfigurationFormFields';
import type { PersonaFormValues } from 'src/pages/ConfigurationPage/utils/getPersonaFormValues';

// Per-field serialization rules for the persona form's submit payload. Adding
// a field with custom serialization means adding an entry here rather than
// growing a switch statement embedded in the submit handler.
const FIELD_TRANSFORMERS: Record<string, (formData: PersonaFormValues) => unknown> = {
  pf_default_language: (formData) =>
    formData.pf_default_language.key ? formData.pf_default_language.key : null,
  pf_excluded_languages: (formData) =>
    formData.pf_excluded_languages?.length
      ? formData.pf_excluded_languages.map((lang) => lang.key)
      : null,
  pf_pages: (formData) => (formData.pf_pages.length ? formData.pf_pages : null),
  pf_words_blacklist: (formData) =>
    formData.pf_words_blacklist.length ? formData.pf_words_blacklist : null,
  pf_cta: (formData) => (formData.pf_role === 'sales' ? formData.pf_cta || null : null),
  pf_icp: (formData) => (formData.pf_role === 'sales' ? formData.pf_icp || null : null),
  pf_tone_of_voice_emoji: (formData) => formData.pf_tone_of_voice_emoji ?? null,
  pf_company_description: (formData) => formData.pf_company_description || '',
};

// Workflow fields are sourced from the current inputData, not the form -
// they're edited via separate workflow state, not the persona form fields.
const WORKFLOW_FIELD_KEYS = new Set(['pf_default_workflow', 'pf_custom_workflows']);

export const buildPersonaSettingsPayload = (
  formData: PersonaFormValues,
  formFieldsObject: ConfigurationFormFieldsObject,
  inputData: Record<string, unknown> | null,
) => {
  const cleanedFormData: Record<string, unknown> = { ...formData };
  delete cleanedFormData.pf_words_blacklist_item;
  delete cleanedFormData.pf_role_click_value;
  delete cleanedFormData.pf_tone_of_voice_style_click_value;

  return Object.keys(formFieldsObject).reduce((payload: Record<string, unknown>, key) => {
    if (WORKFLOW_FIELD_KEYS.has(key)) {
      if (inputData?.[key] !== undefined) {
        payload[key] = inputData[key];
      }
      return payload;
    }

    // Transformers only read persona values, which the cleanup above leaves untouched.
    const transform = FIELD_TRANSFORMERS[key];
    payload[key] = transform ? transform(formData) : cleanedFormData[key] || null;

    return payload;
  }, {});
};
